/* Judge.me reviews — renders sections/ui-section__judgeme-reviews.liquid from
 * Judge.me's public JSON endpoint. No token: the endpoint is the one Judge.me's
 * own widget reads, it answers with CORS `*` and is CDN-cached for 20 minutes.
 *
 * Sort and the rating filter are both server-side (sort_by / sort_dir /
 * filter_rating), so every list state is one request, cached per
 * sort|rating|page. The histogram and total in the response always describe the
 * whole product, filtered or not, so the summary is painted from any response.
 *
 * cdn.judge.me answers 429 on CDN misses only (a cached URL never gets one),
 * and that limit is shared across shops and shoppers — the origin throttles
 * the CDN's own pulls — so a lone request after a long idle can still get it.
 * The 429 carries no CORS header, so in the browser it is not a status but a
 * rejected fetch ("Failed to fetch"). The same path on api.judge.me (the
 * origin, what Judge.me's own widget calls) answers the same JSON with CORS
 * `*` and was not throttled in testing; it just has no edge cache. So: CDN
 * first for the cached fast path, and on any failure the origin at once. If
 * the origin fails too, every request on the page holds off for Judge.me's
 * `Retry-After: 5` (not CORS-exposed, so hard-coded), then backs off.
 */
(function () {
  'use strict';

  var CDN_HOST = 'https://cdn.judge.me/';
  var ORIGIN_HOST = 'https://api.judge.me/';
  // false: skip the CDN and ask the origin first.
  var USE_CDN = false;
  var API = CDN_HOST + 'reviews/reviews_for_widget';
  // Store (shop) reviews: the all-reviews endpoint with review_type. Same
  // sort/filter/page params; always the old widget's `{ html, page, … }`.
  var STORE_API = CDN_HOST + 'reviews/all_reviews_js_based';
  var TIMEOUT = 8000;
  var RETRY_DELAYS = [1500, 3000, 6000];
  var RATE_LIMIT_WAIT = 5000;
  // Shared by every fetchJson on the page: no request starts before it.
  var coolUntil = 0;

  // Section sort value → API params. highest_rating / lowest_rating are NOT
  // accepted verbatim — the endpoint silently falls back to created_at — it
  // wants `rating` plus a direction. most_helpful drops back to created_at if a
  // sort_dir is sent with it, so it gets none.
  var SORT_PARAMS = {
    created_at: { sort_by: 'created_at', sort_dir: 'desc' },
    highest_rating: { sort_by: 'rating', sort_dir: 'desc' },
    lowest_rating: { sort_by: 'rating', sort_dir: 'asc' },
    with_pictures: { sort_by: 'with_pictures' },
    pictures_first: { sort_by: 'pictures_first' },
    videos_first: { sort_by: 'videos_first' },
    most_helpful: { sort_by: 'most_helpful' }
  };

  // body_html / reply_content come from shoppers: rebuild them from this
  // allow-list instead of assigning innerHTML.
  // Pagination arrows. Static markup, never built from response data.
  var CHEVRON_PREV = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_NEXT = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_FIRST = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M16 6l-6 6 6 6M7 6v12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_LAST = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 6l6 6-6 6M17 6v12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var ALLOWED_TAGS ={ P: 1, BR: 1, STRONG: 1, B: 1, EM: 1, I: 1, U: 1, UL: 1, OL: 1, LI: 1 };

  function abortError() {
    var error = new Error('Aborted');
    error.name = 'AbortError';
    return error;
  }

  // `signal` only cancels pending retries — the request itself is never
  // aborted. Other scripts on the storefront wrap window.fetch without a
  // catch, so an aborted fetch surfaces there as "Uncaught (in promise)
  // AbortError". A stale response is cheap (CDN-cached) and still fills the
  // cache; load() just doesn't render it. The timeout races instead of aborting.
  function fetchJson(url, signal) {
    var originUrl = url.indexOf(CDN_HOST) === 0 ? ORIGIN_HOST + url.slice(CDN_HOST.length) : url;

    function attempt(index, target) {
      var wait = coolUntil - Date.now();
      if (wait > 0) {
        return new Promise(function (resolve) { setTimeout(resolve, wait); }).then(function () {
          if (signal.aborted) throw abortError();
          return attempt(index, target);
        });
      }

      var timer;
      var timeout = new Promise(function (resolve, reject) {
        timer = setTimeout(function () { reject(new Error('Timeout')); }, TIMEOUT);
      });

      function cleanup() {
        clearTimeout(timer);
      }

      return Promise.race([fetch(target), timeout]).then(
        function (response) {
          cleanup();
          if (response.ok) return response.json();
          if (response.status === 429 || response.status >= 500) return failed(index, target);
          throw new Error('HTTP ' + response.status);
        },
        function (error) {
          cleanup();
          if (signal.aborted) throw abortError();
          return failed(index, target, error);
        }
      );
    }

    // The CDN's failure (almost always its 429) costs one more round trip,
    // not a wait: the origin is asked straight away. An origin failure is
    // the real rate limit; the whole page pauses, then this one backs off.
    function failed(index, target, error) {
      if (target !== originUrl) return attempt(index, originUrl);
      // Jittered so the requests held back do not all fire at once.
      coolUntil = Math.max(coolUntil, Date.now() + RATE_LIMIT_WAIT * (1 + Math.random() * 0.4));
      return retry(index, error);
    }

    function retry(index, error) {
      if (index >= RETRY_DELAYS.length) throw error || new Error('Too many retries');
      var delay = RETRY_DELAYS[index] * (0.75 + Math.random() * 0.5);
      return new Promise(function (resolve) { setTimeout(resolve, delay); }).then(function () {
        if (signal.aborted) throw abortError();
        return attempt(index + 1, originUrl);
      });
    }

    return attempt(0, USE_CDN ? url : originUrl);
  }

  function copySafe(from, to) {
    Array.prototype.forEach.call(from.childNodes, function (node) {
      if (node.nodeType === 3) {
        to.appendChild(document.createTextNode(node.nodeValue));
        return;
      }
      if (node.nodeType !== 1 || node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      if (ALLOWED_TAGS[node.tagName]) {
        var el = document.createElement(node.tagName.toLowerCase());
        copySafe(node, el);
        to.appendChild(el);
      } else {
        copySafe(node, to);
      }
    });
  }

  function setSafeHtml(target, html) {
    target.textContent = '';
    // DOMParser builds an inert document: no scripts run, no images load.
    copySafe(new DOMParser().parseFromString(html || '', 'text/html').body, target);
  }

  // "Sep 21, 2026", in the shopper's own time zone — the same day Judge.me's
  // widget prints (17:00Z on the 25th reads the 26th in Vietnam). The list and
  // the photo popup share it.
  var dateFormat = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  function formatDate(iso) {
    var time = Date.parse(iso);
    return isNaN(time) ? '' : dateFormat.format(new Date(time));
  }

  function textOf(el, selector) {
    var found = el.querySelector(selector);
    return found ? found.textContent.trim() : '';
  }

  // "2026-09-25 16:32:37 UTC" (store reviews carry only this) → ISO, which
  // every browser parses.
  function utcIso(text) {
    var match = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) UTC$/.exec(text || '');
    return match ? match[1] + 'T' + match[2] + 'Z' : '';
  }

  /* Stores still on Judge.me's old widget get `{ html, total_count, page }`
   * from the same endpoint instead of review data. Read each `.jdgm-rev` into
   * the shape the 2.0 JSON uses — attributes first (data-review-id, data-score,
   * datetime), classes only for the text — so both layouts and the photo
   * viewer render either response. Parsed inert: no scripts, no image loads. */
  function parseLegacyReviews(html) {
    var doc = new DOMParser().parseFromString(html || '', 'text/html');
    return Array.prototype.map.call(doc.querySelectorAll('.jdgm-rev'), function (el) {
      var rating = el.querySelector('.jdgm-rev__rating');
      var time = el.querySelector('.jdgm-rev__timestamp');
      var body = el.querySelector('.jdgm-rev__body');
      var reply = el.querySelector('.jdgm-rev__reply-content');
      return {
        uuid: el.getAttribute('data-review-id') || '',
        rating: rating ? parseInt(rating.getAttribute('data-score'), 10) || 0 : 0,
        created_at: time ? time.getAttribute('datetime') || utcIso(time.getAttribute('data-content')) : '',
        reviewer_name: textOf(el, '.jdgm-rev__author'),
        reviewer_initial: textOf(el, '.jdgm-rev__icon'),
        verified_buyer: el.getAttribute('data-verified-buyer') === 'true',
        title: textOf(el, '.jdgm-rev__title'),
        body_html: body ? body.innerHTML : '',
        thumb_up: parseInt(el.getAttribute('data-thumb-up-count'), 10) || 0,
        // The old widget only carries a 160px thumbnail and the 1024px original.
        pictures_urls: Array.prototype.map.call(el.querySelectorAll('.jdgm-rev__pic-link'), function (link) {
          var img = link.querySelector('img');
          var small = img && (img.getAttribute('data-src') || img.getAttribute('src'));
          var huge = link.getAttribute('data-mfp-src') || link.getAttribute('href');
          return { small: small || huge, huge: huge || small };
        }),
        video_external_ids: Array.prototype.map.call(el.querySelectorAll('.jdgm-vid-player[data-external-id]'), function (player) {
          return player.getAttribute('data-external-id');
        }),
        media_platform_hosted_video_infos: Array.prototype.map.call(el.querySelectorAll('.jdgm-yt-video[data-id]'), function (video) {
          return { media_platform_name: 'yt', media_platform_url: video.getAttribute('data-id') };
        }),
        reply_content: reply && reply.textContent.trim() ? reply.innerHTML : '',
        shop_reply_name: textOf(el, '.jdgm-rev__replier'),
        product_variant_title: ''
      };
    });
  }

  // `total_count` counts the current query (a filter or "Only Pictures"
  // narrows it), so it sizes the pager; the product's own total and average
  // come from Liquid, the way the 2.0 response reports them.
  function normalizeResponse(data, root) {
    if (!data || Array.isArray(data.reviews) || typeof data.html !== 'string') return data;
    var perPage = parseInt(root.dataset.perPage, 10) || 5;
    var queryTotal = parseInt(data.total_count, 10) || 0;
    return {
      reviews: parseLegacyReviews(data.html),
      number_of_reviews: parseInt(root.dataset.count, 10) || queryTotal,
      average_rating: root.dataset.average,
      pagination: {
        total_pages: Math.ceil(queryTotal / perPage),
        current_page: parseInt(data.page, 10) || 1,
        per_page: perPage
      }
    };
  }

  /* A review's photos then videos, as one list: `thumb` for the small tiles,
   * `full` for the big picture; videos add `embed` for a player and `play`
   * for one that starts on its own. Videos
   * resolve the way Judge.me's widgets do (common.js): a UUID is Bunny Stream,
   * a longer id Cloudflare Stream, anything else Vimeo; YouTube links come in
   * their own list. Muted like the store's `mute_video_by_default`, so the
   * player may start on its own. */
  var BUNNY_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var GALLERY_MORE_ICON = '<span class="uid-jdgm__gallery-more" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg></span>';
  var PLAY_ICON = '<span class="uid-jdgm-play" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg></span>';

  function videoMedia(vendor, id) {
    var key = encodeURIComponent(id);
    var video = { video: true };
    if (vendor === 'yt') {
      video.thumb = 'https://img.youtube.com/vi/' + key + '/hqdefault.jpg';
      video.embed = 'https://www.youtube-nocookie.com/embed/' + key + '?mute=1&playsinline=1';
      video.play = video.embed + '&autoplay=1';
    } else if (vendor === 'bunny') {
      video.thumb = 'https://review-videos.judgeme.com/' + key + '/thumbnail.jpg';
      video.embed = 'https://iframe.mediadelivery.net/embed/748386/' + key + '?muted=true&preload=false&responsive=true';
      video.play = video.embed + '&autoplay=true';
    } else if (vendor === 'cloudflare') {
      video.thumb = 'https://videodelivery.net/' + key + '/thumbnails/thumbnail.jpg?time=1s&height=320';
      video.embed = 'https://iframe.videodelivery.net/' + key + '?muted=1&controls=1&playsinline=1';
      video.play = video.embed + '&autoplay=1';
    } else {
      video.thumb = 'https://i.vimeocdn.com/video/' + key + '_200.jpg';
      video.embed = 'https://player.vimeo.com/video/' + key + '?muted=1&playsinline=1';
      video.play = video.embed + '&autoplay=1';
    }
    video.full = video.thumb;
    return video;
  }

  function reviewMedia(review) {
    var media = [];
    (review.pictures_urls || []).forEach(function (p) {
      if (p && (p.small || p.huge)) {
        media.push({ thumb: p.small || p.compact || p.huge, full: p.huge || p.original || p.small });
      }
    });
    (review.video_external_ids || []).forEach(function (id) {
      if (!id) return;
      id = String(id);
      media.push(videoMedia(BUNNY_ID.test(id) ? 'bunny' : id.length > 16 ? 'cloudflare' : 'vimeo', id));
    });
    (review.media_platform_hosted_video_infos || []).forEach(function (info) {
      if (info && info.media_platform_name === 'yt' && info.media_platform_url) {
        media.push(videoMedia('yt', String(info.media_platform_url)));
      }
    });
    return media;
  }

  // Store reviews: `number_of_shop_reviews` counts the current query (a rating
  // filter narrows it) but only comes with page 1, so later pages reuse the
  // count page 1 of the same query reported (`known`).
  function normalizeStoreResponse(data, perPage, known) {
    var total = data.number_of_shop_reviews != null ? parseInt(data.number_of_shop_reviews, 10) || 0 : known || 0;
    return {
      reviews: parseLegacyReviews(data.html),
      number_of_reviews: total,
      pagination: {
        total_pages: Math.ceil(total / perPage),
        current_page: parseInt(data.page, 10) || 1,
        per_page: perPage
      }
    };
  }

  /* Happy customers page: the all-reviews endpoint, every product's reviews
   * (or, with review_type, the store's). Asked without the 2025 widget's
   * `widget_type` it answers the old widget's HTML on old and new widget
   * stores alike and honours `per_page`; with it, JSON at a fixed 10. Page 1
   * carries both counts, for the query (a rating filter narrows them); later
   * pages reuse what page 1 reported (`known`). A JSON answer is read too. */
  function normalizeAllReviews(data, perPage, known, tab) {
    data = data || {};
    var total;
    var reviews;
    if (Array.isArray(data.reviews)) {
      reviews = data.reviews;
      total = parseInt((data.pagination || {}).total_count, 10) || 0;
    } else {
      reviews = parseLegacyReviews(data.html);
      var count = tab === 'store' ? data.number_of_shop_reviews : data.number_of_product_reviews;
      total = count != null ? parseInt(count, 10) || 0 : known || 0;
    }
    return {
      reviews: reviews,
      number_of_reviews: total,
      number_of_product_reviews: data.number_of_product_reviews,
      number_of_shop_reviews: data.number_of_shop_reviews,
      pagination: {
        total_pages: Math.ceil(total / perPage),
        current_page: parseInt(data.page, 10) || 1,
        per_page: perPage
      }
    };
  }

  function setStars(container, rating) {
    var fill = container.querySelector('[data-jdgm-stars-fill]');
    if (fill) fill.style.width = Math.max(0, Math.min(100, (rating / 5) * 100)) + '%';
  }

  // Every page while they fit in one row; past seven, first, last and the
  // current page's neighbours, with a gap marker (null) between.
  function pageList(current, total) {
    var out = [];
    for (var n = 1; n <= total; n++) {
      if (total <= 7) {
        out.push(n);
        continue;
      }
      if (n === 1 || n === total || Math.abs(n - current) <= 1) out.push(n);
      else if (out[out.length - 1] !== null) out.push(null);
    }
    return out;
  }

  // Old-widget pager: a window of three page numbers that follows the current
  // page; first/previous and next/last arrows reach the rest.
  function pageWindow(current, total) {
    var start = Math.max(1, Math.min(current - 1, total - 2));
    var out = [];
    for (var n = start; n <= Math.min(total, start + 2); n++) out.push(n);
    return out;
  }

  function setup(root) {
    var layout = root.dataset.layout === 'v1' ? 'v1' : 'v2';
    var defaultSort = SORT_PARAMS[root.dataset.defaultSort] ? root.dataset.defaultSort : 'pictures_first';

    /* Product reviews and store reviews (new widget's second tab) each have a
     * panel with its own list, pager, query and cache. `useSource` points the
     * shared names below — list, els.empty/error/pagination, state, cache —
     * at the active one, so loading and rendering read whichever tab is open. */
    function panelSource(name) {
      var panel = root.querySelector('[data-jdgm-panel="' + name + '"]');
      if (!panel || !panel.querySelector('[data-jdgm-list]')) return null;
      return {
        name: name,
        list: panel.querySelector('[data-jdgm-list]'),
        // The Liquid placeholders, shown again whenever a page has to load.
        skeleton: panel.querySelector('[data-jdgm-list]').innerHTML,
        empty: panel.querySelector('[data-jdgm-empty]'),
        error: panel.querySelector('[data-jdgm-error]'),
        pagination: panel.querySelector('[data-jdgm-pagination]'),
        state: { sort: defaultSort, rating: 0, page: 1 },
        cache: {},
        // Store totals by query (sort|rating), from page 1.
        totals: {},
        firstPaint: true
      };
    }
    var sources = { product: panelSource('product'), store: panelSource('store') };
    var source = sources.product;
    var list = source && source.list;
    var template = root.querySelector('[data-jdgm-review-template]');
    var cardTemplate = root.querySelector('[data-jdgm-card-template]');
    if (!list || !(layout === 'v1' ? cardTemplate : template)) return;

    var els = {
      toolbar: root.querySelector('[data-jdgm-toolbar]'),
      sort: root.querySelector('[data-jdgm-sort]'),
      empty: root.querySelector('[data-jdgm-empty]'),
      error: root.querySelector('[data-jdgm-error]'),
      pagination: root.querySelector('[data-jdgm-pagination]'),
      controls: root.querySelector('[data-jdgm-controls]'),
      averageValue: root.querySelector('[data-jdgm-average-value]'),
      averageText: root.querySelector('[data-jdgm-average-text]'),
      averageStars: root.querySelector('[data-jdgm-average-stars]'),
      score: root.querySelector('[data-jdgm-score]'),
      productCount: root.querySelector('[data-jdgm-tab-count="product"]'),
      filtersToggle: root.querySelector('[data-jdgm-filters-toggle]'),
      filtersPanel: root.querySelector('[data-jdgm-filters-panel]'),
      write: root.querySelector('[data-jdgm-write]')
    };

    var state = source.state;
    var cache = source.cache;
    // Every photo on the rendered page, in order, each with its review.
    var pageShots = [];
    var controller = null;
    var started = false;

    function useSource(next) {
      source = next;
      list = next.list;
      state = next.state;
      cache = next.cache;
      els.empty = next.empty;
      els.error = next.error;
      els.pagination = next.pagination;
    }

    var perPage = parseInt(root.dataset.perPage, 10) || 5;

    var allReviews = root.dataset.pageType === 'store';

    function url(src) {
      var params = SORT_PARAMS[src.state.sort];
      var shop = encodeURIComponent(root.dataset.shopDomain);
      if (allReviews) {
        var all = [
          'url=' + shop,
          'shop_domain=' + shop,
          'platform=shopify',
          'page=' + src.state.page,
          'sort_by=' + params.sort_by,
          'per_page=' + perPage
        ];
        if (src.name === 'store') all.push('review_type=shop-reviews');
        if (params.sort_dir) all.push('sort_dir=' + params.sort_dir);
        if (src.state.rating) all.push('filter_rating=' + src.state.rating);
        return STORE_API + '?' + all.join('&');
      }
      var query = src.name === 'store' ? [
        'url=' + shop,
        'shop_domain=' + shop,
        'platform=shopify',
        'review_type=shop-reviews'
      ] : [
        'product_id=' + encodeURIComponent(root.dataset.productId),
        'shop_domain=' + shop,
        'platform=shopify',
        'widget_theme=' + (root.dataset.widgetTheme || 'standard')
      ];
      query.push(
        'per_page=' + perPage,
        'page=' + src.state.page,
        'sort_by=' + params.sort_by
      );
      if (params.sort_dir) query.push('sort_dir=' + params.sort_dir);
      if (src.state.rating) query.push('filter_rating=' + src.state.rating);
      return (src.name === 'store' ? STORE_API : API) + '?' + query.join('&');
    }

    /* Pagination "Load more": "Show More" fetches the next page and the list
     * grows. The list is always rendered whole — every loaded page of the
     * query, from cache — so a tab switch or a re-render keeps them all. */
    var loadMore = root.dataset.pagination === 'load_more';
    var moreQueued = false;

    function setBusy(busy) {
      list.setAttribute('aria-busy', busy ? 'true' : 'false');
      // Loading more: the button spins; the list on screen stays as it is.
      var more = moreQueued && els.pagination.querySelector('[data-jdgm-more]');
      if (more) {
        more.disabled = busy;
        more.classList.toggle('is-loading', busy);
        if (!busy) moreQueued = false;
      }
    }

    // A page that has to load shows the placeholders again, the list held at
    // its current height meanwhile so nothing below jumps up and back down.
    // (The first load already shows them from Liquid.)
    function showSkeleton(src) {
      if (src.firstPaint) return;
      src.list.style.minHeight = src.list.offsetHeight + 'px';
      src.list.innerHTML = src.skeleton;
      // The masonry must not deal the old cards back in over them.
      if (layout === 'v1' && src === source) cards = [];
    }

    // Load more: this query's pages 1..current joined, as long as all are in
    // cache (they are, being loaded one after another).
    function withEarlierPages(data) {
      if (!loadMore || state.page < 2) return data;
      var reviews = [];
      for (var page = 1; page <= state.page; page++) {
        var cached = cache[state.sort + '|' + state.rating + '|' + page];
        if (!cached) return data;
        reviews = reviews.concat(cached.reviews || []);
      }
      return Object.assign({}, data, { reviews: reviews });
    }

    function load() {
      started = true;
      var src = source;
      src.loaded = true;
      var query = state.sort + '|' + state.rating;
      var key = query + '|' + state.page;
      if (controller) controller.abort();
      controller = null;
      els.error.hidden = true;

      if (cache[key]) {
        render(cache[key]);
        return;
      }

      var mine = new AbortController();
      controller = mine;
      if (!moreQueued) showSkeleton(src);
      setBusy(true);
      fetchJson(url(src), mine.signal)
        .then(function (data) {
          if (allReviews) {
            data = normalizeAllReviews(data, perPage, src.totals[query], src.name);
            src.totals[query] = data.number_of_reviews;
          } else if (src.name === 'store') {
            data = normalizeStoreResponse(data, perPage, src.totals[query]);
            src.totals[query] = data.number_of_reviews;
          } else {
            data = normalizeResponse(data, root);
          }
          // `src`, not the shared names: the shopper may have switched tabs.
          src.cache[key] = data;
          if (controller !== mine) return;
          render(data);
        })
        .catch(function (error) {
          if (controller !== mine || error.name === 'AbortError') return;
          setBusy(false);
          // The retry message replaces the placeholders.
          if (!moreQueued) {
            src.list.textContent = '';
            src.list.style.minHeight = '';
          }
          els.error.hidden = false;
        });
    }

    /* ── Rendering ─────────────────────────────────────────────────────── */

    // Rating bars from Judge.me's shape: [{ rating, frequency, percentage }].
    function fillHistogram(rows) {
      (rows || []).forEach(function (row) {
        var fill = root.querySelector('[data-jdgm-histogram-fill="' + row.rating + '"]');
        var countEl = root.querySelector('[data-jdgm-histogram-count="' + row.rating + '"]');
        if (fill) fill.style.width = (row.percentage || 0) + '%';
        if (countEl) countEl.textContent = String(row.frequency || 0);
      });
    }

    function renderSummary(data) {
      var average = parseFloat(data.average_rating) || 0;
      var count = parseInt(data.number_of_reviews, 10) || 0;

      if (els.averageValue) els.averageValue.textContent = count ? average.toFixed(1) : '';
      if (els.averageStars) {
        setStars(els.averageStars, average);
        els.averageStars.setAttribute('aria-label', root.dataset.starsLabel.replace('[rating]', average));
      }
      if (els.score) {
        els.score.textContent = count ? root.dataset.scorePattern.replace('[rating]', average.toFixed(2)) : '';
      }
      if (els.averageText) {
        var pattern = count === 1 ? root.dataset.summaryOne : root.dataset.summaryOther;
        els.averageText.textContent = count ? pattern.replace('[count]', count) : '';
      }
      if (els.productCount) els.productCount.textContent = '(' + count + ')';

      fillHistogram(data.histogram);

      // A product with no reviews has nothing to sort or filter.
      if (els.controls) els.controls.hidden = count === 0;
      root.classList.toggle('uid-jdgm--empty', count === 0);
    }

    function fillReview(node, review) {
      var stars = node.querySelector('[data-jdgm-review-stars]');
      setStars(stars, review.rating);
      stars.setAttribute('aria-label', root.dataset.starsLabel.replace('[rating]', review.rating));

      var date = node.querySelector('[data-jdgm-date]');
      date.setAttribute('datetime', review.created_at || '');
      date.textContent = formatDate(review.created_at);

      var name = review.reviewer_name || root.dataset.anonymous;
      node.querySelector('[data-jdgm-name]').textContent = name;
      node.querySelector('[data-jdgm-initial]').textContent =
        review.reviewer_initial || name.charAt(0).toUpperCase();

      var title = node.querySelector('[data-jdgm-title]');
      if (review.title) title.textContent = review.title;
      else title.remove();

      setSafeHtml(node.querySelector('[data-jdgm-body]'), review.body_html);

      var variant = node.querySelector('[data-jdgm-variant]');
      if (review.product_variant_title) variant.textContent = review.product_variant_title;
      else variant.remove();

      var pictures = node.querySelector('[data-jdgm-pictures]');
      var shots = reviewMedia(review);
      if (!shots.length) {
        pictures.remove();
      } else {
        shots.forEach(function (media) {
          // Index into every photo and video on this page: the viewer slides
          // across reviews.
          var at = pageShots.push({ media: media, review: review }) - 1;
          var item = document.createElement('li');
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'uid-jdgm-review__picture';
          button.setAttribute('aria-label', (media.video ? 'Play customer video ' : 'Open customer photo ') + (at + 1));
          var img = document.createElement('img');
          img.src = media.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          button.appendChild(img);
          if (media.video) button.insertAdjacentHTML('beforeend', PLAY_ICON);
          button.addEventListener('click', function () {
            openLightbox(at, button);
          });
          item.appendChild(button);
          pictures.appendChild(item);
        });
      }

      // Absent when the "Show store replies" setting is off.
      var reply = node.querySelector('[data-jdgm-reply]');
      if (!reply) {
        // nothing to fill
      } else if (review.reply_content) {
        node.querySelector('[data-jdgm-reply-name]').textContent = review.shop_reply_name || '';
        setSafeHtml(node.querySelector('[data-jdgm-reply-body]'), review.reply_content);
      } else {
        reply.remove();
      }

      return node;
    }

    /* ── Old-widget layout: masonry cards ── */

    // Cards of the rendered page, in API order; the masonry deals them out.
    var cards = [];

    function fillCard(node, review) {
      var shots = reviewMedia(review);
      var photo = node.querySelector('[data-jdgm-card-photo]');
      if (shots.length) {
        // Every photo and video of the review joins the viewer; the card
        // shows the first photo on top, as the legacy widget does.
        var first = pageShots.length;
        shots.forEach(function (media) { pageShots.push({ media: media, review: review }); });
        var cover = shots.filter(function (media) { return !media.video; })[0];
        if (cover) {
          photo.querySelector('img').src = cover.full;
          photo.addEventListener('click', function () { openLightbox(first, photo); });
        } else {
          photo.remove();
        }
      } else {
        photo.remove();
      }

      // Videos play in the card itself, under the text.
      var videos = node.querySelector('[data-jdgm-card-videos]');
      shots.filter(function (media) { return media.video; }).forEach(function (media) {
        var frame = document.createElement('iframe');
        frame.src = media.embed;
        frame.title = 'Review video';
        frame.loading = 'lazy';
        frame.allow = 'fullscreen; picture-in-picture; encrypted-media';
        frame.allowFullscreen = true;
        videos.appendChild(frame);
      });
      if (!videos.children.length) videos.remove();

      node.querySelector('[data-jdgm-name]').textContent = review.reviewer_name || root.dataset.anonymous;

      var stars = node.querySelector('[data-jdgm-review-stars]');
      setStars(stars, review.rating);
      stars.setAttribute('aria-label', root.dataset.starsLabel.replace('[rating]', review.rating));

      var title = node.querySelector('[data-jdgm-title]');
      if (review.title) title.textContent = review.title;
      else title.remove();

      setSafeHtml(node.querySelector('[data-jdgm-body]'), review.body_html);
      return node;
    }

    function columnCount() {
      return parseInt(getComputedStyle(list).getPropertyValue('--uid-jdgm-columns'), 10) || 1;
    }

    // "Read more" only where the clamp actually cut the text.
    function syncReadMore(card) {
      var body = card.querySelector('[data-jdgm-body]');
      var more = card.querySelector('[data-jdgm-read-more]');
      if (!body || !more) return;
      more.hidden = card.classList.contains('is-expanded') || body.scrollHeight <= body.clientHeight + 1;
    }

    // Masonry: each card goes into the column that is currently shortest, so
    // the grid reads left to right like Judge.me's own. CSS columns would fill
    // top to bottom instead. Card photos have a fixed ratio, so every height is
    // known when it is measured.
    function layoutMasonry() {
      var count = columnCount();
      var columns = [];
      list.textContent = '';
      list.jdgmColumns = count;
      // Hidden (zero width) means nothing can be measured yet; the resize
      // observer lays it out again once it shows.
      list.jdgmUnmeasured = list.offsetWidth === 0;
      for (var i = 0; i < count; i++) {
        var column = document.createElement('div');
        column.className = 'uid-jdgm-v1__column';
        list.appendChild(column);
        columns.push(column);
      }
      cards.forEach(function (card) {
        var shortest = columns[0];
        columns.forEach(function (column) {
          if (column.offsetHeight < shortest.offsetHeight) shortest = column;
        });
        shortest.appendChild(card);
        syncReadMore(card);
      });
    }

    function renderGrid(reviews) {
      pageShots = [];
      var rendered = cards = reviews.map(function (review) {
        return fillCard(cardTemplate.content.firstElementChild.cloneNode(true), review);
      });
      layoutMasonry();
      // Text measured in the fallback font fits differently once the web font
      // lands: deal the cards again then, so heights and "Read more" match.
      if (document.fonts && document.fonts.status !== 'loaded') {
        document.fonts.ready.then(function () {
          if (cards === rendered) layoutMasonry();
        });
      }
    }

    if (layout === 'v1' && typeof ResizeObserver !== 'undefined') {
      // Each tab has its own grid; `list` is the open one, and a grid that was
      // hidden (zero width) is laid out again once it shows.
      // Work happens a frame later: laying the grid out inside the callback
      // resizes what is observed, which browsers report as a loop error.
      var gridQueued = false;
      var gridObserver = new ResizeObserver(function () {
        if (gridQueued) return;
        gridQueued = true;
        requestAnimationFrame(function () {
          gridQueued = false;
          if (!cards.length) return;
          if (list.jdgmUnmeasured || columnCount() !== list.jdgmColumns) layoutMasonry();
          // Same columns, new width: the clamp may now cut, or no longer cut.
          else cards.forEach(syncReadMore);
        });
      });
      [sources.product, sources.store].forEach(function (src) {
        if (src) gridObserver.observe(src.list);
      });
    }

    function renderList(data) {
      var reviews = data.reviews || [];
      list.textContent = '';
      pageShots = [];
      if (layout === 'v1') {
        renderGrid(reviews);
      } else {
        reviews.forEach(function (review) {
          list.appendChild(fillReview(template.content.firstElementChild.cloneNode(true), review));
        });
      }

      var total = parseInt(data.number_of_reviews, 10) || 0;
      els.empty.hidden = reviews.length > 0;
      if (!reviews.length) {
        // Product: any review at all means a filter emptied the list. Store:
        // the total already counts the filter, so ask the query instead.
        var narrowed = source.name === 'store' || allReviews ? state.rating || state.sort === 'with_pictures' : total;
        els.empty.textContent = narrowed ? els.empty.dataset.emptyFilter : els.empty.dataset.emptyAll;
      }
    }

    function renderPagination(data) {
      var nav = els.pagination;
      var total = (data.pagination && data.pagination.total_pages) || 0;
      nav.textContent = '';
      nav.hidden = total < 2;
      if (total < 2) return;

      if (loadMore) {
        nav.hidden = state.page >= total;
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'uid-jdgm__more';
        more.setAttribute('data-jdgm-more', '');
        more.innerHTML = '<span></span>';
        more.firstChild.textContent = root.dataset.labelMore || 'Show More';
        nav.appendChild(more);
        return;
      }

      function button(label, page, options) {
        var el = document.createElement('button');
        el.type = 'button';
        el.className = 'uid-jdgm__page' + (options.className ? ' ' + options.className : '');
        if (options.icon) el.innerHTML = options.icon;
        else el.textContent = label;
        if (options.ariaLabel) el.setAttribute('aria-label', options.ariaLabel);
        if (options.current) el.setAttribute('aria-current', 'page');
        if (options.disabled) el.disabled = true;
        else el.setAttribute('data-jdgm-page', String(page));
        nav.appendChild(el);
      }

      if (layout === 'v1') {
        if (state.page > 1) {
          button('', 1, { className: 'uid-jdgm__page--icon', icon: CHEVRON_FIRST, ariaLabel: 'First page' });
          button('', state.page - 1, { className: 'uid-jdgm__page--icon', icon: CHEVRON_PREV, ariaLabel: 'Previous page' });
        }
        pageWindow(state.page, total).forEach(function (page) {
          button(String(page), page, { current: page === state.page, ariaLabel: 'Page ' + page });
        });
        if (state.page < total) {
          button('', state.page + 1, { className: 'uid-jdgm__page--icon', icon: CHEVRON_NEXT, ariaLabel: 'Next page' });
          button('', total, { className: 'uid-jdgm__page--icon', icon: CHEVRON_LAST, ariaLabel: 'Last page' });
        }
        return;
      }

      button('', state.page - 1, { className: 'uid-jdgm__page--arrow', icon: CHEVRON_PREV, ariaLabel: 'Previous page', disabled: state.page <= 1 });
      pageList(state.page, total).forEach(function (page) {
        if (page === null) {
          var gap = document.createElement('span');
          gap.className = 'uid-jdgm__page-gap';
          gap.textContent = '…';
          nav.appendChild(gap);
        } else {
          button(String(page), page, { current: page === state.page, ariaLabel: 'Page ' + page });
        }
      });
      button('', state.page + 1, { className: 'uid-jdgm__page--arrow', icon: CHEVRON_NEXT, ariaLabel: 'Next page', disabled: state.page >= total });
    }

    function render(data) {
      var unfiltered = !state.rating && state.sort !== 'with_pictures';
      if (allReviews) {
        // Header from Liquid (shop metafields) and, when the app embed put it
        // on the page, Judge.me's all-reviews data; tab counts from any
        // unfiltered answer, which carries both.
        renderAllReviewsHeader();
        if (unfiltered) {
          setTabCount(els.productCount, data.number_of_product_reviews);
          setStoreCount(data.number_of_shop_reviews);
        }
      } else if (source.name === 'product') {
        renderSummary(data);
        renderGallery(data);
        prefetchStore();
      }
      else if (unfiltered) setStoreCount(data.number_of_reviews);
      renderList(withEarlierPages(data));
      list.style.minHeight = '';
      renderPagination(data);
      source.firstPaint = false;
      setBusy(false);
    }

    var storeCount = root.querySelector('[data-jdgm-tab-count="store"]');

    /* Photo gallery (new widget): a row of customer photos from the response's
     * `photo_gallery` — the same whatever the sort, filter or page, so it is
     * built once. Each tile opens the photo viewer over the whole gallery. The
     * old widget's HTML response has no gallery; the row stays hidden. */
    var gallery = root.querySelector('[data-jdgm-gallery]');
    var galleryShots = null;

    function renderGallery(data) {
      if (!gallery || galleryShots) return;
      galleryShots = [];
      // Placeholder tiles go; with nothing to show the row goes too.
      gallery.textContent = '';
      if (!Array.isArray(data.photo_gallery) || !data.photo_gallery.length) {
        gallery.hidden = true;
        syncGalleryNext();
        return;
      }
      data.photo_gallery.forEach(function (entry) {
        // Gallery entries date as "2026-09-30 11:01:27 UTC".
        var review = Object.assign({}, entry, { created_at: utcIso(entry.created_at) || entry.created_at });
        reviewMedia(review).forEach(function (media) {
          galleryShots.push({ media: media, review: review });
        });
      });
      galleryShots.forEach(function (shot, index) {
        var item = document.createElement('li');
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'uid-jdgm__gallery-item';
        button.setAttribute('aria-label', (shot.media.video ? 'Play customer video ' : 'Open customer photo ') + (index + 1));
        var img = document.createElement('img');
        img.src = shot.media.full;
        img.alt = '';
        img.width = 400;
        img.height = 400;
        img.loading = 'lazy';
        img.decoding = 'async';
        button.appendChild(img);
        if (shot.media.video) button.insertAdjacentHTML('beforeend', PLAY_ICON);
        // Shown by CSS on the last visible tile when more photos follow.
        button.insertAdjacentHTML('beforeend', GALLERY_MORE_ICON);
        button.addEventListener('click', function () {
          openLightbox(index, button, galleryShots);
        });
        item.appendChild(button);
        gallery.appendChild(item);
      });
      // Large tiles need the setting and at least three to fill the row
      // (Judge.me's rule); otherwise small thumbnails.
      gallery.classList.toggle('is-compact', root.dataset.galleryExpanded !== 'true' || galleryShots.length < 3);
      gallery.hidden = !galleryShots.length;
      syncGalleryNext();
    }

    // Phones show the gallery as a sideways strip (CSS); the arrow steps it one
    // tile on and goes away at the end, or wherever the strip does not scroll.
    var galleryNext = root.querySelector('[data-jdgm-gallery-next]');

    function syncGalleryNext() {
      if (!galleryNext) return;
      galleryNext.hidden = gallery.hidden ||
        gallery.scrollLeft + gallery.clientWidth >= gallery.scrollWidth - 2;
    }

    if (gallery && galleryNext) {
      galleryNext.addEventListener('click', function () {
        var tile = gallery.firstElementChild;
        var step = tile ? tile.getBoundingClientRect().width : gallery.clientWidth;
        gallery.scrollBy({ left: step, behavior: 'smooth' });
      });
      gallery.addEventListener('scroll', syncGalleryNext, { passive: true });
      window.addEventListener('resize', syncGalleryNext);
    }

    function setTabCount(el, count) {
      if (el && count != null) el.textContent = '(' + (parseInt(count, 10) || 0) + ')';
    }

    function setStoreCount(count) {
      setTabCount(storeCount, count);
    }

    // window.jdgm.data.allReviewsWidgetV2025 — only there when Judge.me's
    // all-reviews app block is on the page — has the average, histogram and
    // photo gallery over all reviews. Without it the Liquid header stays: the
    // average from shop metafields and the rating bars read from the
    // all_reviews_header metafield (data-jdgm-histogram-static). Where that
    // metafield is all zeros (new-widget stores), the bars are counted from
    // the endpoint instead.
    var allHeaderDone = false;

    function renderAllReviewsHeader() {
      if (allHeaderDone) return;
      allHeaderDone = true;
      var widgetData = null;
      try {
        widgetData = window.jdgm && window.jdgm.data && window.jdgm.data.allReviewsWidgetV2025;
      } catch (error) { /* no app embed */ }
      var histogram = root.querySelector('.uid-jdgm__histogram');
      if (widgetData && Array.isArray(widgetData.histogram) && widgetData.histogram.length) {
        renderSummary(Object.assign({}, widgetData, {
          average_rating: widgetData.average_rating != null ? widgetData.average_rating : root.dataset.average,
          number_of_reviews: widgetData.number_of_reviews != null ? widgetData.number_of_reviews : root.dataset.count
        }));
      } else if (histogram && !histogram.hasAttribute('data-jdgm-histogram-static')) {
        countHistogram(histogram);
      }
      renderGallery(widgetData || {});
    }

    // One tiny request per rating (per_page=1, CDN-cached): page 1 reports
    // the product and store review counts for that filter; together they are
    // every review at that rating. One after another, gentle on the rate
    // limit. A failure hides the bars rather than show zeros.
    function countHistogram(histogram) {
      var shop = encodeURIComponent(root.dataset.shopDomain);
      var counts = {};
      var ratings = [5, 4, 3, 2, 1];
      ratings.reduce(function (chain, rating) {
        return chain.then(function () {
          return fetchJson(STORE_API + '?url=' + shop + '&shop_domain=' + shop +
            '&platform=shopify&per_page=1&page=1&sort_by=created_at&filter_rating=' + rating,
            new AbortController().signal)
            .then(function (data) {
              counts[rating] = (parseInt(data.number_of_product_reviews, 10) || 0) +
                (parseInt(data.number_of_shop_reviews, 10) || 0);
            });
        });
      }, Promise.resolve()).then(function () {
        var total = ratings.reduce(function (sum, rating) { return sum + counts[rating]; }, 0);
        if (!total) throw new Error('no reviews');
        fillHistogram(ratings.map(function (rating) {
          return { rating: rating, frequency: counts[rating], percentage: Math.round(counts[rating] / total * 100) };
        }));
      }).catch(function () {
        histogram.hidden = true;
      });
    }

    // Controls show the open tab's query.
    function syncControls() {
      if (els.sort) els.sort.value = state.sort;
      syncFilterButtons();
    }

    function syncFilterButtons() {
      root.querySelectorAll('[data-jdgm-filter]').forEach(function (el) {
        var on = Number(el.getAttribute('data-jdgm-filter')) === state.rating;
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // The panel is usually closed, so the toggle is what tells the shopper
      // a filter is still narrowing the list.
      if (els.filtersToggle) els.filtersToggle.classList.toggle('is-active', state.rating > 0);
    }

    function setFiltersOpen(open) {
      if (!els.filtersPanel || !els.filtersToggle) return;
      els.filtersPanel.hidden = !open;
      els.filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function selectTab(name) {
      root.querySelectorAll('[data-jdgm-tab]').forEach(function (tab) {
        var on = tab.getAttribute('data-jdgm-tab') === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      root.querySelectorAll('[data-jdgm-panel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-jdgm-panel') !== name;
      });
      setFiltersOpen(false);
      // Filters and sort act on the open tab; its first visit loads page 1.
      if (!sources[name] || sources[name] === source) return;
      if (controller) controller.abort();
      controller = null;
      useSource(sources[name]);
      syncControls();
      // Always render again (from cache once seen): the photo viewer follows
      // the list on screen.
      load();
    }

    // The store tab shows its count before it is opened: page 1 of its query
    // is fetched once the product reviews are in. CDN-cached, and the tab then
    // opens from cache.
    function prefetchStore() {
      var src = sources.store;
      if (allReviews || !src || src.loaded || src.prefetched) return;
      src.prefetched = true;
      var query = src.state.sort + '|' + src.state.rating;
      fetchJson(url(src), new AbortController().signal).then(function (data) {
        data = normalizeStoreResponse(data, perPage, src.totals[query]);
        src.totals[query] = data.number_of_reviews;
        src.cache[query + '|1'] = data;
        setStoreCount(data.number_of_reviews);
      }).catch(function () {});
    }

    // After a page change from the bottom of a long list, bring the shopper
    // back to the top of the reviews rather than leaving them below them.
    function scrollToList() {
      var anchor = els.toolbar && !els.toolbar.hidden ? els.toolbar : list;
      if (anchor.getBoundingClientRect().top < 0) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* ── Controls ──────────────────────────────────────────────────────── */

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-jdgm-filters-toggle]')) {
        setFiltersOpen(els.filtersPanel.hidden);
        return;
      }

      var tab = event.target.closest('[data-jdgm-tab]');
      if (tab) {
        selectTab(tab.getAttribute('data-jdgm-tab'));
        return;
      }

      var filter = event.target.closest('[data-jdgm-filter]');
      if (filter) {
        // The histogram counts product reviews, so it filters those.
        if (!filter.closest('[data-jdgm-filters-panel]') && source !== sources.product) selectTab('product');
        var rating = Number(filter.getAttribute('data-jdgm-filter'));
        state.rating = state.rating === rating ? 0 : rating;
        state.page = 1;
        syncFilterButtons();
        load();
        return;
      }

      if (event.target.closest('[data-jdgm-more]')) {
        moreQueued = true;
        state.page += 1;
        load();
        return;
      }

      var page = event.target.closest('[data-jdgm-page]');
      if (page) {
        state.page = Number(page.getAttribute('data-jdgm-page')) || 1;
        load();
        scrollToList();
        return;
      }

      var more = event.target.closest('[data-jdgm-read-more]');
      if (more) {
        more.closest('article').classList.add('is-expanded');
        more.hidden = true;
        return;
      }

      if (event.target.closest('[data-jdgm-retry]')) load();
    });

    if (els.sort) {
      els.sort.addEventListener('change', function () {
        state.sort = SORT_PARAMS[els.sort.value] ? els.sort.value : state.sort;
        state.page = 1;
        load();
      });
    }

    // The filter panel closes on a click anywhere outside it, and on Escape.
    document.addEventListener('click', function (event) {
      if (els.filtersPanel && !els.filtersPanel.hidden && !event.target.closest('[data-jdgm-filters-toggle], [data-jdgm-filters-panel]')) {
        setFiltersOpen(false);
      }
    });
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !els.filtersPanel || els.filtersPanel.hidden) return;
      setFiltersOpen(false);
      els.filtersToggle.focus();
    });


    // "Write a review" opens the section's own form: in place on the old layout,
    // a modal on the new one. It used to click the app block's button, whose form
    // opened inside that block — elsewhere on the page, or not at all.
    if (els.write && layout === 'v1') {
      setupInlineForm(root, els.write);
    } else if (els.write) {
      var reviewForm = setupReviewForm(root);
      if (reviewForm) {
        els.write.hidden = false;
        els.write.addEventListener('click', function () {
          reviewForm.open(els.write);
        });
      }
    }

    /* ── Lightbox ──────────────────────────────────────────────────────── */

    /* The track is a native scroll-snap row, so a swipe is the browser's own
     * scroll; the arrows and keys scroll it too. The review beside the photo
     * follows whichever slide the track has settled on. */

    var lightbox = root.querySelector('[data-jdgm-lightbox]');
    var lb = null;
    var lbShots = [];
    var lbIndex = 0;
    var opener = null;

    if (lightbox) {
      lb = {
        track: lightbox.querySelector('[data-jdgm-lightbox-track]'),
        counter: lightbox.querySelector('[data-jdgm-lightbox-counter]'),
        prev: lightbox.querySelector('[data-jdgm-lightbox-prev]'),
        next: lightbox.querySelector('[data-jdgm-lightbox-next]'),
        close: lightbox.querySelector('[data-jdgm-lightbox-close]'),
        thumbs: lightbox.querySelector('[data-jdgm-lightbox-thumbs]'),
        initial: lightbox.querySelector('[data-jdgm-lightbox-initial]'),
        name: lightbox.querySelector('[data-jdgm-lightbox-name]'),
        stars: lightbox.querySelector('[data-jdgm-lightbox-stars]'),
        date: lightbox.querySelector('[data-jdgm-lightbox-date]'),
        title: lightbox.querySelector('[data-jdgm-lightbox-review-title]'),
        text: lightbox.querySelector('[data-jdgm-lightbox-text]')
      };

      // Portal to <body> so no ancestor's stacking context traps the overlay.
      // A Theme Editor re-render leaves the previous copy behind — drop it.
      var stale = document.getElementById(lightbox.id);
      if (stale && stale !== lightbox) stale.remove();
      document.body.appendChild(lightbox);
    }

    function fillInfo(review) {
      var name = review.reviewer_name || root.dataset.anonymous;
      lb.name.textContent = name;
      lb.initial.textContent = review.reviewer_initial || name.charAt(0).toUpperCase();

      setStars(lb.stars, review.rating);
      lb.stars.setAttribute('aria-label', root.dataset.starsLabel.replace('[rating]', review.rating));

      lb.date.setAttribute('datetime', review.created_at || '');
      lb.date.textContent = formatDate(review.created_at);

      lb.title.textContent = review.title || '';
      lb.title.hidden = !review.title;
      setSafeHtml(lb.text, review.body_html);
    }

    function setLbIndex(index) {
      lbIndex = index;
      lb.counter.textContent = index + 1 + ' / ' + lbShots.length;
      fillInfo(lbShots[index].review);

      // Mark the active thumbnail and bring it into the middle of the strip.
      // Scrolls the strip only — scrollIntoView would also move the page.
      Array.prototype.forEach.call(lb.thumbs.children, function (item, n) {
        var button = item.firstElementChild;
        button.setAttribute('aria-current', n === index ? 'true' : 'false');
        if (n !== index) return;
        var offset = item.offsetLeft - (lb.thumbs.clientWidth - item.offsetWidth) / 2;
        lb.thumbs.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
      });
      // Warm the neighbours so the next swipe doesn't wait on the network.
      [index - 1, index, index + 1].forEach(function (n) {
        var slide = lb.track.children[n];
        var img = slide && slide.querySelector('img');
        if (img) img.loading = 'eager';
      });
      syncVideos();
    }

    // Only the video in view has a player: it starts (muted) when its slide
    // comes up, and leaving the slide removes the player, which stops it.
    function syncVideos() {
      Array.prototype.forEach.call(lb.track.children, function (slide, n) {
        var embed = slide.getAttribute('data-jdgm-embed');
        if (!embed) return;
        var frame = slide.querySelector('iframe');
        if (n !== lbIndex) {
          if (frame) frame.remove();
        } else if (!frame) {
          frame = document.createElement('iframe');
          frame.src = embed;
          frame.title = 'Review video';
          frame.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
          frame.allowFullscreen = true;
          slide.appendChild(frame);
        }
      });
    }

    // The slide our own scroll is heading for. While it is set, the scroll
    // listener leaves the review alone: the first frames of a smooth scroll
    // still rest on the old slide, and reading them as "settled" flipped the
    // review back to the old one before the new one landed.
    var scrollTarget = null;

    function scrollToShot(index, instant) {
      var left = index * lb.track.clientWidth;
      if (Math.abs(lb.track.scrollLeft - left) < 2) {
        scrollTarget = null;
        return;
      }
      scrollTarget = index;
      if (instant) lb.track.scrollLeft = left;
      else lb.track.scrollTo({ left: left, behavior: 'smooth' });
    }

    // Swipe support: as soon as another slide fills most of the stage —
    // past the halfway point, not once the snap has come to rest — show its
    // thumbnail, counter and review, as the arrows do straight away.
    function syncFromScroll() {
      var width = lb.track.clientWidth || 1;
      var index = Math.round(lb.track.scrollLeft / width);
      var settled = Math.abs(lb.track.scrollLeft - index * width) < 2;
      if (scrollTarget !== null) {
        if (settled && index === scrollTarget) scrollTarget = null;
        return;
      }
      if (index !== lbIndex && lbShots[index]) setLbIndex(index);
    }

    function go(step) {
      var count = lbShots.length;
      if (count < 2) return;
      var next = (lbIndex + step + count) % count;
      // Wrapping round jumps instead of sweeping past every photo.
      scrollToShot(next, Math.abs(next - lbIndex) > 1);
      setLbIndex(next);
    }

    // Jump straight to a photo picked from the strip.
    function goTo(index) {
      if (index === lbIndex || !lbShots[index]) return;
      scrollToShot(index, Math.abs(index - lbIndex) > 1);
      setLbIndex(index);
    }

    function buildSlides() {
      lb.track.textContent = '';
      lb.thumbs.textContent = '';
      lb.thumbs.hidden = lbShots.length < 2;
      lbShots.forEach(function (shot, i) {
        var thumb = document.createElement('li');
        var thumbButton = document.createElement('button');
        thumbButton.type = 'button';
        thumbButton.className = 'uid-jdgm-lightbox__thumb';
        thumbButton.setAttribute('data-jdgm-lightbox-thumb', String(i));
        thumbButton.setAttribute('aria-label', (shot.media.video ? 'Video ' : 'Photo ') + (i + 1) + ' of ' + lbShots.length);
        var thumbImg = document.createElement('img');
        thumbImg.src = shot.media.thumb;
        thumbImg.alt = '';
        thumbImg.width = 160;
        thumbImg.height = 160;
        thumbImg.loading = 'lazy';
        thumbImg.decoding = 'async';
        thumbButton.appendChild(thumbImg);
        if (shot.media.video) thumbButton.insertAdjacentHTML('beforeend', PLAY_ICON);
        thumb.appendChild(thumbButton);
        lb.thumbs.appendChild(thumb);

        var slide = document.createElement('li');
        slide.className = 'uid-jdgm-lightbox__slide';
        slide.setAttribute('aria-label', i + 1 + ' / ' + lbShots.length);
        // A video slide shows its still until syncVideos puts the player on it.
        if (shot.media.video) slide.setAttribute('data-jdgm-embed', shot.media.play);
        var img = document.createElement('img');
        img.src = shot.media.full;
        img.alt = '';
        img.width = 1024;
        img.height = 1024;
        img.loading = 'lazy';
        img.decoding = 'async';
        slide.appendChild(img);
        lb.track.appendChild(slide);
      });
    }

    // `shots` defaults to the reviews on screen; the photo gallery passes its own.
    function openLightbox(index, trigger, shots) {
      shots = shots || pageShots;
      if (!lb || !shots[index]) return;
      lbShots = shots.slice();
      opener = trigger;
      buildSlides();
      lb.prev.hidden = lbShots.length < 2;
      lb.next.hidden = lbShots.length < 2;
      lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      setLbIndex(index);
      scrollToShot(index, true);
      lb.close.focus({ focusVisible: false });
    }

    function closeLightbox() {
      if (!lb || lightbox.hidden) return;
      lightbox.hidden = true;
      scrollTarget = null;
      lb.track.textContent = '';
      lb.thumbs.textContent = '';
      document.documentElement.style.overflow = '';
      if (opener) opener.focus();
      opener = null;
    }

    function trapTab(event) {
      var stops = Array.prototype.filter.call(lightbox.querySelectorAll('button'), function (el) {
        return !el.closest('[hidden]');
      });
      var first = stops[0];
      var last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!lightbox.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    if (lb) {
      lightbox.addEventListener('click', function (event) {
        if (event.target === lightbox || event.target.closest('[data-jdgm-lightbox-close]')) closeLightbox();
        else if (event.target.closest('[data-jdgm-lightbox-prev]')) go(-1);
        else if (event.target.closest('[data-jdgm-lightbox-next]')) go(1);
        else {
          var thumb = event.target.closest('[data-jdgm-lightbox-thumb]');
          if (thumb) goTo(Number(thumb.getAttribute('data-jdgm-lightbox-thumb')));
        }
      });

      document.addEventListener('keydown', function (event) {
        if (lightbox.hidden) return;
        if (event.key === 'Escape') closeLightbox();
        else if (event.key === 'ArrowLeft') go(-1);
        else if (event.key === 'ArrowRight') go(1);
        else if (event.key === 'Tab') trapTab(event);
      });

      var settleQueued = false;
      lb.track.addEventListener('scroll', function () {
        if (settleQueued) return;
        settleQueued = true;
        requestAnimationFrame(function () {
          settleQueued = false;
          syncFromScroll();
        });
      }, { passive: true });

      // Where supported, scrollend is the authoritative "it stopped" — also
      // clears a target an interrupted smooth scroll never reached.
      lb.track.addEventListener('scrollend', function () {
        scrollTarget = null;
        syncFromScroll();
      });

      // The shopper taking over mid-animation hands control back to the swipe.
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (type) {
        lb.track.addEventListener(type, function () { scrollTarget = null; }, { passive: true });
      });

      // A resize changes the slide width; keep the same photo in view.
      window.addEventListener('resize', function () {
        if (!lightbox.hidden) scrollToShot(lbIndex, true);
      });
    }

    /* ── Start ─────────────────────────────────────────────────────────── */

    // Reviews sit far below the fold: wait until the section is close so the
    // request neither competes with the product page's own loading nor spends
    // the rate limit on shoppers who never scroll that far.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        if (!started) load();
      }, { rootMargin: '600px 0px' });
      observer.observe(root);
    } else {
      load();
    }
  }

  /* ── Write a review ──────────────────────────────────────────────────── */

  /* Two forms: Judge.me's 2.0 modal flow (setupReviewForm) and the legacy
   * widget's one-page form (setupInlineForm). Both post exactly
   * what Judge.me's widgets post to the same public endpoint: FormData with
   * the shop params, no token, so a simple CORS request. Photos follow their
   * uploader: GET a presigned S3 target, upload each file as it is picked,
   * send the keys as `picture_keys[n]`. */
  var REVIEW_API = 'https://api.judge.me/api/v1/reviews';
  var PRESIGN_API = 'https://api.judge.me/api/v1/pictures/presigned_data';
  var MAX_PHOTOS = 5;
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var REMOVE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>';

  function shopParams(shop) {
    return 'url=' + encodeURIComponent(shop) +
      '&shop_domain=' + encodeURIComponent(shop) + '&platform=shopify';
  }

  // Judge.me's normalisation, kept identical so S3's policy accepts the key:
  // strip anything outside [a-z0-9-_.]; no basename left → a timestamp.
  function uploadName(file) {
    var stripped = String(file.name || '').replace(/[^a-z0-9-_.]/gi, '');
    var parts = stripped.split('.');
    var ext = parts.pop();
    if (!parts.join('').length) stripped = Date.now() + '.' + ext;
    return encodeURIComponent(stripped);
  }

  // One presigned target per form, asked for once and reused for every file.
  function createUploader(shop) {
    var presigned = null;

    function target() {
      if (!presigned) {
        presigned = fetch(PRESIGN_API + '?' + shopParams(shop))
          .then(function (response) {
            if (!response.ok) throw new Error('presign ' + response.status);
            return response.json();
          })
          .catch(function (error) {
            presigned = null; // the next pick asks again
            throw error;
          });
      }
      return presigned;
    }

    function upload(file) {
      return target().then(function (data) {
        var key = data.key_prefix + uploadName(file);
        var body = new FormData();
        // Key first, policy fields, file last — S3 rejects any other order.
        body.append('key', key);
        Object.keys(data.fields || {}).forEach(function (name) {
          body.append(name, data.fields[name]);
        });
        body.append('file', file);
        return fetch(data.url, { method: 'POST', body: body }).then(function (response) {
          if (!response.ok) throw new Error('upload ' + response.status);
          return key;
        });
      });
    }

    return {
      // Asking early means the first photo picked does not wait on it.
      prefetch: function () { target().catch(function () {}); },
      upload: upload
    };
  }

  // Inline field errors: `[data-jdgm-form-error="<field name>"]` under each
  // field, and aria-invalid on the field itself.
  function fieldErrors(scope, form) {
    function show(name, message) {
      var el = scope.querySelector('[data-jdgm-form-error="' + name + '"]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
      var input = form.elements[name];
      if (input && input.setAttribute) {
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }
    return {
      show: show,
      clear: function () {
        scope.querySelectorAll('[data-jdgm-form-error]').forEach(function (el) {
          show(el.getAttribute('data-jdgm-form-error'), '');
        });
      }
    };
  }

  // Rating radios under star SVGs: every star up to the chosen one is filled
  // (`is-on`); hovering previews a rating, leaving shows the chosen one again.
  function ratingStars(form, onPick) {
    var stars = form.querySelectorAll('.uid-jdgm-form__star');

    function value() {
      var picked = form.querySelector('input[name="rating"]:checked');
      return picked ? Number(picked.value) : 0;
    }

    function paint(count) {
      stars.forEach(function (star, index) {
        star.classList.toggle('is-on', index < count);
      });
    }

    stars.forEach(function (star, index) {
      star.addEventListener('mouseenter', function () { paint(index + 1); });
    });
    form.querySelector('.uid-jdgm-form__stars').addEventListener('mouseleave', function () {
      paint(value());
    });
    form.addEventListener('change', function (event) {
      if (event.target.name !== 'rating') return;
      paint(value());
      onPick();
    });

    return { value: value, paint: paint };
  }

  // Picked photos: a preview each in `list` (dimmed while uploading, with a
  // remove button), uploaded as soon as they are added. `messages` is the
  // element carrying the data-error-* texts.
  function photoList(list, uploader, messages, showError, onChange) {
    var photos = [];

    function remove(photo) {
      photos = photos.filter(function (p) { return p !== photo; });
      photo.item.remove();
      URL.revokeObjectURL(photo.preview);
      onChange();
    }

    function add(files) {
      showError('');
      Array.prototype.forEach.call(files, function (file) {
        if (!/^image\//.test(file.type)) return;
        if (photos.length >= MAX_PHOTOS) { showError(messages.dataset.errorFileCount); return; }
        if (file.size > MAX_PHOTO_BYTES) { showError(messages.dataset.errorFileSize); return; }

        var photo = { key: null, preview: URL.createObjectURL(file) };
        var item = document.createElement('li');
        item.className = 'uid-jdgm-form__preview is-uploading';
        var img = document.createElement('img');
        img.src = photo.preview;
        img.alt = '';
        item.appendChild(img);
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'uid-jdgm-form__preview-remove';
        button.setAttribute('aria-label', 'Remove photo');
        button.innerHTML = REMOVE_ICON;
        button.addEventListener('click', function () { remove(photo); });
        item.appendChild(button);
        list.appendChild(item);
        photo.item = item;
        photos.push(photo);

        photo.upload = uploader.upload(file).then(function (key) {
          photo.key = key;
          item.classList.remove('is-uploading');
        }, function () {
          // A photo that cannot be uploaded would be silently missing from
          // the review; take it out and say so instead.
          remove(photo);
          showError(messages.dataset.errorSubmit);
        });
      });
      onChange();
    }

    return {
      add: add,
      count: function () { return photos.length; },
      clear: function () { photos.slice().forEach(remove); },
      // Photos still uploading are waited for; failed ones already left.
      settled: function () {
        return Promise.all(photos.map(function (p) { return p.upload; }));
      },
      keys: function () {
        return photos.filter(function (p) { return p.key; }).map(function (p) { return p.key; });
      }
    };
  }

  function bindDropzone(zone, input, add) {
    input.addEventListener('change', function () {
      add(input.files);
      input.value = '';
    });
    ['dragenter', 'dragover'].forEach(function (type) {
      zone.addEventListener(type, function (event) {
        event.preventDefault();
        zone.classList.add('is-dragging');
      });
    });
    ['dragleave', 'drop'].forEach(function (type) {
      zone.addEventListener(type, function (event) {
        event.preventDefault();
        zone.classList.remove('is-dragging');
        if (type === 'drop' && event.dataTransfer) add(event.dataTransfer.files);
      });
    });
  }

  // Required name/email/body + a rating, as both of Judge.me's forms ask.
  // Returns the field to focus first, or null when everything is fine.
  function invalidField(form, errors, rating, messages, fields) {
    var first = null;
    function fail(name, message, el) {
      errors.show(name, message);
      first = first || el;
    }
    fields.forEach(function (name) {
      if (name === 'rating') {
        if (!rating.value()) fail('rating', messages.dataset.errorRating || messages.dataset.errorRequired, form.querySelector('input[name="rating"]'));
        return;
      }
      var input = form.elements[name];
      var value = input.value.trim();
      if (!value) fail(name, messages.dataset.errorRequired, input);
      else if (name === 'email' && !EMAIL_PATTERN.test(value)) fail(name, messages.dataset.errorEmail, input);
    });
    return first;
  }

  function sendReview(root, form, rating, photos, nameFormat) {
    var shop = root.dataset.shopDomain;
    var value = function (name) {
      return form.elements[name] ? form.elements[name].value.trim() : '';
    };
    return photos.settled().then(function () {
      var data = new FormData();
      data.append('url', shop);
      data.append('shop_domain', shop);
      data.append('platform', 'shopify');
      if (root.dataset.pageType === 'store') {
        // A store review, as Judge.me's modal sends one: product id 0.
        data.append('id', '0');
        data.append('judgeme_review_source', 'store-review-collection');
      } else {
        data.append('id', root.dataset.productId);
      }
      data.append('rating', String(rating.value()));
      data.append('name', value('name'));
      data.append('email', value('email'));
      if (value('title')) data.append('title', value('title'));
      data.append('body', value('body'));
      // Judge.me leaves empty fields out; '' is the full-name format.
      if (nameFormat) data.append('reviewer_name_format', nameFormat);
      photos.keys().forEach(function (key, index) {
        data.append('picture_keys[' + index + ']', key);
      });
      return fetch(REVIEW_API, { method: 'POST', body: data });
    }).then(function (response) {
      if (!response.ok) throw new Error('review ' + response.status);
    });
  }

  function setLoading(button, on) {
    button.disabled = on;
    button.classList.toggle('is-loading', on);
    button.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* The 2.0 modal: 1 rating + review, 2 photos (optional), 3 about you,
   * 4 thank you (widget/write_review_modal.js). */
  function setupReviewForm(root) {
    var modal = root.querySelector('[data-jdgm-form]');
    if (!modal) return null;
    var form = modal.querySelector('[data-jdgm-form-element]');

    var ui = {
      progress: modal.querySelector('[data-jdgm-form-progress]'),
      back: modal.querySelector('[data-jdgm-form-back]'),
      next: modal.querySelector('[data-jdgm-form-next]'),
      submit: modal.querySelector('[data-jdgm-form-submit]'),
      done: modal.querySelector('[data-jdgm-form-done]')
    };
    var step = 1;
    var opener = null;
    var sending = false;
    var uploader = createUploader(root.dataset.shopDomain);
    var errors = fieldErrors(modal, form);
    var rating = ratingStars(form, function () { errors.show('rating', ''); });
    var photos = photoList(
      modal.querySelector('[data-jdgm-form-previews]'),
      uploader,
      modal,
      function (message) { errors.show('media', message); },
      syncNextLabel
    );

    // Portal to <body> like the photo viewer; drop a copy a Theme Editor
    // re-render left behind.
    var stale = document.getElementById(modal.id);
    if (stale && stale !== modal) stale.remove();
    document.body.appendChild(modal);

    // Nothing picked on the photos step yet: "Skip" instead of "Next".
    function syncNextLabel() {
      ui.next.textContent = step === 2 && !photos.count() ? modal.dataset.labelSkip : modal.dataset.labelNext;
    }

    function goTo(next) {
      step = next;
      modal.querySelectorAll('[data-jdgm-form-page]').forEach(function (page) {
        page.hidden = Number(page.getAttribute('data-jdgm-form-page')) !== step;
      });
      ui.progress.style.width = Math.min(step, 3) / 3 * 100 + '%';
      ui.back.hidden = step === 1 || step === 4;
      ui.next.hidden = step > 2;
      ui.submit.hidden = step !== 3;
      ui.done.hidden = step !== 4;
      syncNextLabel();
      // Move focus to the new step's title — only while open: this also runs
      // once at setup, and focusing a hidden dialog would scroll the page.
      var title = modal.querySelector('[data-jdgm-form-page="' + step + '"] .uid-jdgm-form__title');
      if (title && !modal.hidden) {
        title.setAttribute('tabindex', '-1');
        title.focus({ focusVisible: false });
      }
    }

    function validateStep() {
      errors.clear();
      var fields = step === 1 ? ['rating', 'body'] : step === 3 ? ['name', 'email'] : [];
      var first = invalidField(form, errors, rating, modal, fields);
      if (first) first.focus();
      return !first;
    }

    function submit() {
      if (sending || !validateStep()) return;
      sending = true;
      setLoading(ui.submit, true);
      ui.back.disabled = true;
      var nameFormat = form.elements.anonymous.checked ? 'anonymous' : '';
      sendReview(root, form, rating, photos, nameFormat).then(function () {
        goTo(4);
      }, function () {
        errors.show('submit', modal.dataset.errorSubmit);
      }).then(function () {
        sending = false;
        setLoading(ui.submit, false);
        ui.back.disabled = false;
      });
    }

    function reset() {
      form.reset();
      photos.clear();
      rating.paint(0);
      errors.clear();
      goTo(1);
    }

    function open(trigger) {
      opener = trigger || null;
      // A finished review starts a fresh form; an unfinished one is kept.
      if (step === 4) reset();
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      goTo(step);
    }

    function close() {
      if (modal.hidden) return;
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      if (step === 4) reset();
      if (opener) opener.focus();
      opener = null;
    }

    function trapTab(event) {
      var stops = Array.prototype.filter.call(
        modal.querySelectorAll('button, input, textarea, a[href]'),
        function (el) { return !el.disabled && !el.closest('[hidden]') && el.offsetParent !== null; }
      );
      if (!stops.length) return;
      var first = stops[0];
      var last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!modal.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    bindDropzone(
      modal.querySelector('[data-jdgm-form-dropzone]'),
      modal.querySelector('[data-jdgm-form-file]'),
      photos.add
    );

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (step === 3) submit();
    });

    modal.addEventListener('click', function (event) {
      if (event.target === modal || event.target.closest('[data-jdgm-form-close], [data-jdgm-form-done]')) {
        close();
      } else if (event.target.closest('[data-jdgm-form-back]')) {
        errors.clear();
        goTo(step - 1);
      } else if (event.target.closest('[data-jdgm-form-next]')) {
        if (validateStep()) goTo(step + 1);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (modal.hidden) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'Tab') trapTab(event);
    });

    modal.addEventListener('focusin', uploader.prefetch, { once: true });

    goTo(1);
    return { open: open, close: close };
  }

  /* The legacy widget's form (templates/shopify_v3/review_form): one page
   * that opens in place, the "Write a review" button turning into "Cancel
   * review". After sending, the form gives way to a notice and the button
   * becomes "Refresh page", as on Judge.me. */
  function setupInlineForm(root, button) {
    var wrapper = root.querySelector('[data-jdgm-inline-form]');
    if (!wrapper) return;
    var form = wrapper.querySelector('[data-jdgm-form-element]');
    var notice = root.querySelector('[data-jdgm-form-notice]');
    var submitButton = wrapper.querySelector('[data-jdgm-form-submit]');
    var openLabel = button.textContent;
    var sending = false;
    var sent = false;
    var uploader = createUploader(root.dataset.shopDomain);
    var errors = fieldErrors(wrapper, form);
    var rating = ratingStars(form, function () { errors.show('rating', ''); });
    var photos = photoList(
      wrapper.querySelector('[data-jdgm-form-previews]'),
      uploader,
      wrapper,
      function (message) { errors.show('media', message); },
      function () {}
    );

    // Characters left, next to the label, as Judge.me counts them.
    [['title', 100], ['body', 5000]].forEach(function (entry) {
      var input = form.elements[entry[0]];
      var count = wrapper.querySelector('[data-jdgm-form-count="' + entry[0] + '"]');
      if (!input || !count) return;
      input.addEventListener('input', function () {
        count.textContent = '(' + (entry[1] - input.value.length) + ')';
      });
    });

    function setOpen(open) {
      wrapper.hidden = !open;
      button.textContent = open ? wrapper.dataset.labelCancel : openLabel;
      button.setAttribute('aria-expanded', String(open));
      if (!open) return;
      uploader.prefetch();
      var first = form.querySelector('input[type="text"], textarea');
      if (first) first.focus();
    }

    function submit() {
      if (sending) return;
      errors.clear();
      var first = invalidField(form, errors, rating, wrapper, ['rating', 'body', 'name', 'email']);
      if (first) {
        first.focus();
        return;
      }
      sending = true;
      setLoading(submitButton, true);
      sendReview(root, form, rating, photos, form.elements.reviewer_name_format.value).then(function () {
        sent = true;
        setOpen(false);
        button.textContent = wrapper.dataset.labelRefresh;
        button.removeAttribute('aria-expanded');
        notice.hidden = false;
        notice.focus();
      }, function () {
        errors.show('submit', wrapper.dataset.errorSubmit);
      }).then(function () {
        sending = false;
        setLoading(submitButton, false);
      });
    }

    bindDropzone(
      wrapper.querySelector('[data-jdgm-form-dropzone]'),
      wrapper.querySelector('[data-jdgm-form-file]'),
      photos.add
    );

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      submit();
    });

    wrapper.querySelector('[data-jdgm-form-close]').addEventListener('click', function () {
      setOpen(false);
      button.focus();
    });

    button.setAttribute('aria-controls', wrapper.id);
    button.setAttribute('aria-expanded', 'false');
    button.hidden = false;
    button.addEventListener('click', function () {
      if (sent) window.location.reload();
      else setOpen(wrapper.hidden);
    });
  }

  /* ── Preview badge ─────────────────────────────────────────────────────── */

  // Judge.me's preview badge (app block) only scrolls to Judge.me's own
  // widget, which this section replaces, so a click on it does nothing. Point
  // the product's badge here instead; product-card badges carry other ids.
  function reviewsFor(badge) {
    var id = badge.getAttribute('data-id');
    return id ? document.querySelector('[data-jdgm-reviews][data-product-id="' + id + '"]') : null;
  }

  function linkPreviewBadges() {
    document.querySelectorAll('.jdgm-preview-badge[data-id]').forEach(function (badge) {
      if (reviewsFor(badge)) badge.classList.add('uid-jdgm-badge-link');
    });
  }

  document.addEventListener('click', function (event) {
    var badge = event.target.closest('.jdgm-preview-badge[data-id]');
    var root = badge && reviewsFor(badge);
    if (!root) return;
    var header = document.querySelector('.section-header--sticky');
    window.scrollTo({
      top: root.getBoundingClientRect().top + window.pageYOffset - (header ? header.offsetHeight : 0),
      behavior: 'smooth'
    });
  });

  function init(scope) {
    (scope || document).querySelectorAll('[data-jdgm-reviews]').forEach(function (root) {
      if (root.dataset.jdgmBound) return;
      root.dataset.jdgmBound = '1';
      setup(root);
    });
    linkPreviewBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme Editor re-renders a section into a fresh subtree.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
